import React from 'react';

import { getCurrentContentTitle } from '@lumapps/contents/ducks/selectors';
import { useSelector } from '@lumapps/redux/react';
import { standardizeTranslateObject, useTranslate } from '@lumapps/translations';
import { inputLanguageSelector } from '@lumapps/translations/ducks/selectors';
import { document } from '@lumapps/wrex/document/structured-content';
import { isRichTextContentEmpty } from '@lumapps/wrex/serialization/slate/utils/isRichTextContentEmpty';
import { getFirstContentAsString } from '@lumapps/wrex/slate/utils/getFirstContentAsString';
import type { Wrex } from '@lumapps/wrex/types';

import type { LegacyWidgetContributionWrapperProps } from '../components/LegacyWidgetContributionWrapper';
import { getRichTextFromProperties } from '../utils/getRichTextFromProperties';
import { useDocumentV2Upload } from './useDocumentV2Upload';

export const useEditableWidgetContribution = ({
    legacyWidget,
    switchToReadMode,
    saveTextualContentInWidgetProperties,
}: Pick<
    LegacyWidgetContributionWrapperProps,
    'legacyWidget' | 'switchToReadMode' | 'saveTextualContentInWidgetProperties'
>) => {
    const wrapperRef = React.useRef(null);
    const childrenRefs = React.useRef([wrapperRef]);
    const contentTitle = useSelector(getCurrentContentTitle);
    const currentInputLang = useSelector(inputLanguageSelector);
    const widgetProperties = legacyWidget?.properties;
    const { translateObject } = useTranslate();

    /** we use slate format to easily switch from read to edit without having to parse every time */
    const getInitialContent = React.useCallback(
        (currentInputLang: string) => getRichTextFromProperties(widgetProperties, currentInputLang),
        [widgetProperties],
    );

    /** on click outside the editor, we save the slate content in the widget properties and switch to read mode */
    const exitEditionMode = React.useCallback(
        (content: Wrex.Nodes) => {
            const newRichText = document.parse({
                children: content,
                title: translateObject(standardizeTranslateObject(contentTitle)) || '',
                description: getFirstContentAsString(content),
            });

            /**
             * Reset rich-text for current lang if:
             * - the new rich-text is empty
             * - there was a previous entry for this lang
             * */
            if (isRichTextContentEmpty(newRichText) && widgetProperties?.richText?.[currentInputLang]) {
                saveTextualContentInWidgetProperties({
                    ...widgetProperties?.richText,
                    [currentInputLang]: undefined,
                });
            } else if (!isRichTextContentEmpty(newRichText)) {
                saveTextualContentInWidgetProperties({
                    ...widgetProperties?.richText,
                    [currentInputLang]: newRichText,
                });
            }

            switchToReadMode();
        },
        [
            contentTitle,
            currentInputLang,
            widgetProperties,
            saveTextualContentInWidgetProperties,
            switchToReadMode,
            translateObject,
        ],
    );

    const onFileUpload = useDocumentV2Upload();

    return {
        wrapperRef,
        exitEditionMode,
        childrenRefs,
        onFileUpload,
        getInitialContent,
    };
};
